import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { AppLayout } from '../components/AppLayout';
import { UserService } from '../services/userService';
import { User } from '../types/index';
import '../styles/AdminUsersPage.css';

export const AdminUsersPage: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [pendingUsers, setPendingUsers] = useState<User[]>([]);
  const [approvedUsers, setApprovedUsers] = useState<User[]>([]);
  const [deactivatedUsers, setDeactivatedUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'pending' | 'approved' | 'deactivated'>('pending');

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    loadUsers();
  }, [user, navigate]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const [pending, approved, deactivated] = await Promise.all([
        UserService.getPendingUsers(),
        UserService.getApprovedUsers(),
        UserService.getDeactivatedUsers(),
      ]);
      setPendingUsers(pending);
      setApprovedUsers(approved);
      setDeactivatedUsers(deactivated);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveUser = async (userId: string) => {
    try {
      setError(null);
      await UserService.approveUser(userId, user!.uid);
      await loadUsers();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleRejectUser = async (userId: string) => {
    if (window.confirm('Are you sure you want to reject this user?')) {
      try {
        setError(null);
        await UserService.rejectUser(userId);
        await loadUsers();
      } catch (err: any) {
        setError(err.message);
      }
    }
  };

  const handleDeleteUser = async (userId: string) => {
    const userToDelete = pendingUsers.find(u => u.uid === userId) || approvedUsers.find(u => u.uid === userId) || deactivatedUsers.find(u => u.uid === userId);
    if (window.confirm(`Are you sure you want to permanently delete "${userToDelete?.name}"? This cannot be undone.`)) {
      try {
        setError(null);
        await UserService.deleteUser(userId);
        await loadUsers();
      } catch (err: any) {
        setError(err.message);
      }
    }
  };

  const handleChangeRole = async (userId: string, newRole: 'admin' | 'sales' | 'viewer') => {
    try {
      setError(null);
      await UserService.updateUserRole(userId, newRole);
      await loadUsers();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleDeactivateUser = async (userId: string) => {
    const userToDeactivate = approvedUsers.find(u => u.uid === userId);
    if (window.confirm(`Deactivate "${userToDeactivate?.name}"? They will not be able to log in.`)) {
      try {
        setError(null);
        await UserService.deactivateUser(userId);
        await loadUsers();
      } catch (err: any) {
        setError(err.message);
      }
    }
  };

  const handleReactivateUser = async (userId: string) => {
    const userToReactivate = deactivatedUsers.find(u => u.uid === userId);
    if (window.confirm(`Reactivate "${userToReactivate?.name}"?`)) {
      try {
        setError(null);
        await UserService.reactivateUser(userId);
        await loadUsers();
      } catch (err: any) {
        setError(err.message);
      }
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading users...</p>
      </div>
    );
  }

  return (
    <AppLayout>
      <div className="admin-users-container">
        <h1>User Management</h1>
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <div className="tabs">
          <button
            className={`tab ${activeTab === 'pending' ? 'active' : ''}`}
            onClick={() => setActiveTab('pending')}
          >
            Pending Approval ({pendingUsers.length})
          </button>
          <button
            className={`tab ${activeTab === 'approved' ? 'active' : ''}`}
            onClick={() => setActiveTab('approved')}
          >
            Approved Users ({approvedUsers.length})
          </button>
          <button
            className={`tab ${activeTab === 'deactivated' ? 'active' : ''}`}
            onClick={() => setActiveTab('deactivated')}
          >
            Deactivated ({deactivatedUsers.length})
          </button>
        </div>

        <div className="tab-content">
          {activeTab === 'pending' && (
            <div className="users-list">
              {pendingUsers.length === 0 ? (
                <p className="empty-state">No pending users</p>
              ) : (
                pendingUsers.map((userItem) => (
                  <div key={userItem.uid} className="user-card pending">
                    <div className="user-info">
                      <h3>{userItem.name}</h3>
                      <p className="email">{userItem.email}</p>
                      <p className="date">
                        Requested: {new Date(userItem.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="user-actions">
                      <button
                        className="btn-approve"
                        onClick={() => handleApproveUser(userItem.uid)}
                      >
                        Approve
                      </button>
                      <button
                        className="btn-reject"
                        onClick={() => handleRejectUser(userItem.uid)}
                      >
                        Reject
                      </button>
                      <button
                        className="btn-delete"
                        onClick={() => handleDeleteUser(userItem.uid)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {activeTab === 'approved' && (
            <div className="users-list">
              {approvedUsers.length === 0 ? (
                <p className="empty-state">No approved users</p>
              ) : (
                approvedUsers.map((userItem) => (
                  <div key={userItem.uid} className="user-card approved">
                    <div className="user-info">
                      <h3>{userItem.name}</h3>
                      <p className="email">{userItem.email}</p>
                      <p className="date">
                        Approved: {userItem.approvedAt ? new Date(userItem.approvedAt).toLocaleDateString() : 'N/A'}
                      </p>
                    </div>
                    <div className="user-role">
                      <select
                        value={userItem.role}
                        onChange={(e) => handleChangeRole(userItem.uid, e.target.value as any)}
                        className="role-select"
                      >
                        <option value="viewer">Viewer</option>
                        <option value="sales">Sales</option>
                        <option value="admin">Admin</option>
                      </select>
                    </div>
                    <div className="user-actions">
                      <button
                        className="btn-deactivate"
                        onClick={() => handleDeactivateUser(userItem.uid)}
                      >
                        Deactivate
                      </button>
                      <button
                        className="btn-delete"
                        onClick={() => handleDeleteUser(userItem.uid)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {activeTab === 'deactivated' && (
            <div className="users-list">
              {deactivatedUsers.length === 0 ? (
                <p className="empty-state">No deactivated users</p>
              ) : (
                deactivatedUsers.map((userItem) => (
                  <div key={userItem.uid} className="user-card deactivated">
                    <div className="user-info">
                      <h3>{userItem.name}</h3>
                      <p className="email">{userItem.email}</p>
                      <p className="date">
                        Deactivated: {userItem.deactivatedAt ? new Date(userItem.deactivatedAt).toLocaleDateString() : 'N/A'}
                      </p>
                    </div>
                    <div className="user-role">
                      <select
                        value={userItem.role}
                        onChange={(e) => handleChangeRole(userItem.uid, e.target.value as any)}
                        className="role-select"
                      >
                        <option value="viewer">Viewer</option>
                        <option value="sales">Sales</option>
                        <option value="admin">Admin</option>
                      </select>
                    </div>
                    <div className="user-actions">
                      <button
                        className="btn-reactivate"
                        onClick={() => handleReactivateUser(userItem.uid)}
                      >
                        Reactivate
                      </button>
                      <button
                        className="btn-delete"
                        onClick={() => handleDeleteUser(userItem.uid)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
};
